import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SiteBasedHelpdeskManagementResource } from '../../resources/site-based-helpdesk-management-resource.service';
import {
  ClusterResult,
  SiteBasedHelpdeskUserPermission,
  SiteBasedHelpdeskUserPermissions,
  SiteResult,
  SiteSummary,
} from '../../../shared/models/generated/ziroModels';
import { SiteSummaryContext } from '../../../shared/contexts/site-summary.context';
import { SbhPermissionsFilterService } from './sbh-permissions-filter.service';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { Subscription } from 'rxjs';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { AdminEditPermissionsModalComponent } from './edit-permissions-modal.component';
import { BulkEditPermissionsModalComponent } from './bulk-edit-permissions-modal/bulk-edit-permissions-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { LdapResource } from '../../../shared/resources/ldap.resource';
import { TrimWithEllipsisFilter } from '../../../shared/filters/trim-with-ellipsis.filter';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { CucmDnReportRow } from '../../../migrations/migrations-cisco/migrations-cisco-directory-number-report/migrations-cisco-directory-numbers-report.component';

export interface FilterOptionSite {
  site: string;
  cluster: string;
}

interface SiteBasedHelpdeskUserPermissionResult extends SiteBasedHelpdeskUserPermission {
  isSelected: boolean;
}

export interface SiteClusterIntersectionFilter {
  name: string;
  siteNames: string[];
  clusterNames: string[];
  readAccessToReportingEnabled: boolean;
}

export interface SbhPermissionsTableFilterCriteria {
  name: string;
  siteNames: string[];
  clusterNames: string[];
  readAccess: SbhPermissionsReportingOptions;
}

export enum SbhPermissionsReportingOptions {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
}

export interface SbhPermissionsRow extends DatatableRow {
  userId: string;
  authorizedSitesAndClusters: string[];
  control: boolean;
}

@Component({
  selector: 'ziro-site-based-helpdesk-user-permissions-management',
  templateUrl: './site-based-helpdesk-user-permissions-management.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [SbhPermissionsFilterService, TrimWithEllipsisFilter],
  standalone: false,
})
export class SiteBasedHelpdeskUserPermissionsManagementComponent implements OnInit, OnDestroy {
  private siteBasedHelpdeskManagementResource = inject(SiteBasedHelpdeskManagementResource);
  private ldapResource = inject(LdapResource);
  private siteSummaryContext = inject(SiteSummaryContext);
  private ziroModalService = inject(ZiroModalService);
  private toastService = inject(ToastService);
  private bottomNavService = inject(BottomNavService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private translateService = inject(TranslateService);

  tableAutomationId = 'sbh-user-permissions-table';
  rows: SbhPermissionsRow[] = [];
  columns: DatatableColumn<SbhPermissionsRow>[] = [
    {
      name: 'userId',
      label: 'tkey;admin.sitemacman.userIdHeader',
    },
    {
      name: 'authorizedSitesAndClusters',
      label: 'tkey;admin.sitemacman.authorizedSitesHeader',
      columnCharacterWidth: 20,
      sortFn: (a: string[], b: string[]) => (a[0] ?? '').localeCompare(b[0] ?? ''),
    },
    {
      name: 'control',
      label: 'tkey;admin.sitemacman.control.header',
    },
  ];
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ziroIcons = ZiroIcons;
  isLoading = true;
  isUpdateDisplayed = false;
  authorizedSitesFilterOptions: FilterSelectOption[] = [];

  private _permissions: SiteBasedHelpdeskUserPermission[];
  private _clusters: ClusterResult[] = [];
  private _siteSummary: SiteSummary;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.sitemacman.header' }]);

    const siteSummarySub = this.siteSummaryContext.state$.subscribe((siteSummary) => {
      this._siteSummary = siteSummary;
      this._setClustersAndFilterOptions();
      this._getResults();
    });
    this._subscriptions.add(siteSummarySub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  getModalTitle(permissionsToUpdate: SiteBasedHelpdeskUserPermission[]): string {
    return `
      <span>${this.translateService.instant('tkey;admin.sitemacman.modal.title')}</span>
      <small class="x-small px-2">|</small>
      <small class="x-small">${this.translateService.instant('tkey;admin.sitemacman.modal.bulk.edit.selected.users', {
        count: permissionsToUpdate.length,
      })}</small>
   `;
  }

  onUpdatePermissionsClicked(): void {
    const userIdsToUpdate: string[] = this.rows.filter((row) => row.isSelectedInTable).map((row) => row.userId);
    const permissionsToUpdate: SiteBasedHelpdeskUserPermission[] = this._permissions.filter(
      (permission: SiteBasedHelpdeskUserPermission) => userIdsToUpdate.includes(permission.userId),
    );
    const options = {
      modalViewProperties: {
        title: this.getModalTitle(permissionsToUpdate),
        permissionList: permissionsToUpdate,
        authorizedSitesFilterOpts: this.authorizedSitesFilterOptions.map((option) => {
          return {
            site: option.label,
            cluster: option.groupBy,
          };
        }),
        clusters: this._clusters,
        submitButtonLabel: this.translateService.instant('tkey;admin.sitemacman.update_permission', {
          value: permissionsToUpdate.length,
        }),
      },
      bodyClass: BulkEditPermissionsModalComponent,
    };

    this.ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .subscribe((siteBasedHelpdeskUserPermission: SiteBasedHelpdeskUserPermission[]) => {
        if (siteBasedHelpdeskUserPermission && siteBasedHelpdeskUserPermission.length > 0) {
          siteBasedHelpdeskUserPermission.forEach((sitePermission) => {
            this._permissions = this._permissions.map((aPermission: SiteBasedHelpdeskUserPermission) => {
              if (aPermission.userId === sitePermission.userId) {
                return sitePermission;
              }
              return aPermission;
            });
          });
          this._generateTableRows();
          this._refreshBottomNav();
          this.toastService.push(
            ToastTypes.SUCCESS,
            this.ziroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;admin.sitemacman.toast.save.message',
          );
          this.isUpdateDisplayed = false;
        } else {
          this.isUpdateDisplayed = true;
        }
      });
  }

  onRowSelected(): void {
    this._refreshBottomNav();
  }

  isAuthorizedSitesMatch(filters: string[], row: SbhPermissionsRow): boolean {
    return (
      !filters.length ||
      filters.some((filter: string) => {
        const clusterName = this.siteSummaryContext.getClusterNameForSiteName(this._siteSummary, filter);
        const rowSites = row.authorizedSitesAndClusters;
        if (rowSites.includes(clusterName) || rowSites.includes(filter)) {
          return true;
        }
      })
    );
  }

  isControlMatch(filters: string[], row: SbhPermissionsRow): boolean {
    return (
      !filters.length || (filters.includes('Disabled') && !row.control) || (filters.includes('Enabled') && row.control)
    );
  }

  private _getNumberOfSelectedRows(): number {
    return this.rows.filter((row: CucmDnReportRow) => row.isSelectedInTable).length;
  }

  private _refreshBottomNav(): void {
    const selectedRowCount = this._getNumberOfSelectedRows();
    if (selectedRowCount > 1) {
      this.bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'saveSiteBasedAdministrators',
            dataAutomation: 'saveSiteBasedAdministrators',
            label: 'tkey;admin.sitemacman.update_permission',
            type: ButtonTypes.CUSTOM,
            buttonClass: this.buttonStyles.PRIMARY,
            labelParam: selectedRowCount,
            state: {
              pending: false,
              buttonDisableState: { disabled: false, tooltipKey: '' },
            },
            cb: () => this.onUpdatePermissionsClicked(),
          },
        ]),
      );
      this.isUpdateDisplayed = true;
    } else {
      this.isUpdateDisplayed = false;
    }
  }

  private _openEditModal(permission: SiteBasedHelpdeskUserPermission): void {
    if (!permission) {
      return;
    }
    const options = {
      modalViewProperties: {
        title: 'tkey;admin.sitemacman.modal.title',
        authorizedSitesFilterOpts: this.authorizedSitesFilterOptions.map((option) => {
          return {
            site: option.label,
            cluster: option.groupBy,
          };
        }),
        clusters: this._clusters,
        permission,
      },
      bodyClass: AdminEditPermissionsModalComponent,
    };

    this.ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .subscribe((siteBasedHelpdeskUserPermission: SiteBasedHelpdeskUserPermission) => {
        if (siteBasedHelpdeskUserPermission) {
          this._permissions = this._permissions.map((aPermission: SiteBasedHelpdeskUserPermission) => {
            if (aPermission.userId === siteBasedHelpdeskUserPermission.userId) {
              return siteBasedHelpdeskUserPermission;
            }
            return aPermission;
          });
          this._generateTableRows();
          this._refreshBottomNav();
          this.toastService.push(
            ToastTypes.SUCCESS,
            this.ziroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;admin.sitemacman.toast.save.message',
          );
        }
      });
  }

  private _setClustersAndFilterOptions(): void {
    const options: FilterSelectOption[] = [];

    this._clusters = this._siteSummary.clusters;
    this._clusters.forEach((cluster: ClusterResult) => {
      cluster.sites.forEach((site: SiteResult) => {
        options.push({
          label: site.name,
          value: site.name,
          groupBy: cluster.name,
        });
      });
    });

    this.authorizedSitesFilterOptions = [...options];
  }

  private _getResults(): void {
    this.isLoading = true;
    this.ldapResource.getLdapPermissionGroup().subscribe((ldapPermissionGroup) => {
      if (ldapPermissionGroup.siteBasedHelpdeskGroup !== '') {
        this.siteBasedHelpdeskManagementResource.get().subscribe((data: SiteBasedHelpdeskUserPermissions) => {
          this._permissions = data.permissions;
          this._generateTableRows();
          this.isLoading = false;
        });
      } else {
        this.isLoading = false;
      }
    });
  }

  private _generateTableRows(): void {
    this.rows = this._permissions.map((permissionResult: SiteBasedHelpdeskUserPermissionResult) => {
      const authSitesAndClusters = permissionResult.authorizedSites.concat(permissionResult.authorizedClusters);
      return {
        userId: permissionResult.userId,
        authorizedSitesAndClusters: authSitesAndClusters,
        control: permissionResult.readAccessToControlEnabled,
        isSelected: permissionResult.isSelected,
        cssClass: permissionResult.disabled ? 'table-warning' : '',
        isTableRowSelectable: true,
        html: {
          userId: permissionResult.disabled
            ? `<span class="badge bg-warning">${this.translateService.instant(
                'tkey;admin.sitemacman.disabled.badge',
              )}</span><span> ${permissionResult.userId}</span>`
            : permissionResult.userId,
          authorizedSitesAndClusters:
            this._sitesSelected(permissionResult).length < 1
              ? `<div class="text-warning"><i class="${
                  this.ziroIcons.WARNING_TRIANGLE
                } me-1"></i><span>${this.translateService.instant(
                  'tkey;admin.sitemacman.noSiteSelectedWarning',
                )}</span></div>`
              : '',
        },
        badges: {
          authorizedSitesAndClusters: {
            maxLength: 30,
            values: authSitesAndClusters.map((siteCluster) => {
              return { label: siteCluster, showTooltipOnTruncate: true };
            }),
          },
          control: {
            values: permissionResult.readAccessToControlEnabled
              ? [
                  {
                    label: this.translateService.instant('tkey;admin.sitemacman.control.enabled'),
                    icon: this.ziroIcons.CIRCLE_CHECK_OUTLINE,
                    cssClass: 'bg-success',
                  },
                ]
              : [
                  {
                    label: this.translateService.instant('tkey;admin.sitemacman.control.disabled'),
                    icon: this.ziroIcons.FORBIDDEN,
                    cssClass: 'bg-danger',
                  },
                ],
          },
        },
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            buttonSize: ButtonSizes.SMALL,
            dataAutomation: 'site-based-helpdesk-user-edit',
            icon: ZiroIcons.EDIT,
            onClick: () => this._openEditModal(permissionResult),
          },
        ],
      };
    });
  }

  private _sitesSelected(permission: SiteBasedHelpdeskUserPermissionResult): string[] {
    return permission.authorizedSites.concat(permission.authorizedClusters);
  }
}
